import type { StudentTestAnswer } from "p6m-p6u";
import type { ClassMember } from "../networking/schoolClasses";
import { toEpochMs } from "../utils/dateUtils";
import { pickRelevantClassMemberResult } from "./ClassMemberActivities";

export enum MergeConflictStrategy {
    FIRST = "FIRST",
    LAST = "LAST",
}

/** A new result row for the survivor, created from a dropped member's submission. */
export interface MergeCopy {
    testId: string;
    content: StudentTestAnswer;
}

/** The survivor's own row rewritten with the winning submission of a dropped member. */
export interface MergeOverwrite {
    testId: string;
    resultId: string;
    content: StudentTestAnswer;
}

export interface MergePlan {
    survivorId: string;
    droppedIds: string[];
    copies: MergeCopy[];
    overwrites: MergeOverwrite[];
    hasConflicts: boolean;
}

export interface MergeIo {
    createResult: (testId: string, content: StudentTestAnswer) => Promise<unknown>;
    saveResult: (testId: string, resultId: string, content: StudentTestAnswer) => Promise<unknown>;
    deleteMember: (memberId: string) => Promise<unknown>;
}

export const pickSurvivor = (selected: ClassMember[]): ClassMember =>
    [...selected].sort((a, b) => (toEpochMs(a.createdDate) ?? 0) - (toEpochMs(b.createdDate) ?? 0))[0];

const sentTimeOf = (result: StudentTestAnswer): number => result.sentTime ?? 0;

const submissionOf = (result: StudentTestAnswer): StudentTestAnswer => {
    const { id: _id, sharingId: _sharingId, sharedInClassroomDate: _sharedDate, ...submission } = result;
    return submission;
};

/** The share link stays with the row; the winner's release only shows where such a link exists. */
const shareOf = (
    row: StudentTestAnswer,
    winner: StudentTestAnswer
): Pick<StudentTestAnswer, "sharingId" | "sharedInClassroomDate"> => {
    if (!row.sharingId) return {};
    return {
        sharingId: row.sharingId,
        ...(winner.sharedInClassroomDate ? { sharedInClassroomDate: winner.sharedInClassroomDate } : {}),
    };
};

interface MergeCandidate {
    memberId: string;
    relevant: StudentTestAnswer;
    firstSentTime: number;
    lastSentTime: number;
}

const candidateOf = (memberId: string, results: StudentTestAnswer[]): MergeCandidate | null => {
    const own = results.filter((r) => r.classMemberId === memberId);
    const relevant = pickRelevantClassMemberResult(own);
    if (!relevant) return null;
    const sentTimes = own.map(sentTimeOf);
    return { memberId, relevant, firstSentTime: Math.min(...sentTimes), lastSentTime: Math.max(...sentTimes) };
};

const pickWinner = (candidates: MergeCandidate[], strategy: MergeConflictStrategy): MergeCandidate =>
    strategy === MergeConflictStrategy.FIRST
        ? candidates.reduce((a, b) => (b.firstSentTime < a.firstSentTime ? b : a))
        : candidates.reduce((a, b) => (b.lastSentTime >= a.lastSentTime ? b : a));

/**
 * The backend owns a result through a column the frontend cannot change (a PUT only rewrites the
 * content, and deleting a member removes its rows), so the survivor must receive its own row:
 * a POST copy when it had none for the test, or its existing rows overwritten with the winning
 * content.
 *
 * The strategy ranks the members by their own first or last submission; what the survivor receives
 * is the winner's relevant result (its released evaluation, else its latest submission). Every row
 * of the survivor is then overwritten, because a leftover attempt could otherwise outrank the winner
 * as the relevant row and single rows cannot be deleted; a survivor with several attempts therefore
 * shows the winner several times. A copy arrives unshared, an overwritten row keeps its share link
 * and is released only if the winner was.
 */
export const planClassMemberMerge = (params: {
    selected: ClassMember[];
    resultsByTest: Record<string, StudentTestAnswer[]>;
    strategy: MergeConflictStrategy;
}): MergePlan => {
    const survivorId = pickSurvivor(params.selected).id;
    const droppedIds = params.selected.map((m) => m.id).filter((id) => id !== survivorId);
    const copies: MergeCopy[] = [];
    const overwrites: MergeOverwrite[] = [];
    let hasConflicts = false;

    Object.entries(params.resultsByTest).forEach(([testId, results]) => {
        const candidates = params.selected
            .map((member) => candidateOf(member.id, results))
            .filter((candidate): candidate is MergeCandidate => candidate !== null);
        if (candidates.length === 0) return;
        if (candidates.length > 1) hasConflicts = true;

        const winner = pickWinner(candidates, params.strategy);
        if (winner.memberId === survivorId) return;

        const content = { ...submissionOf(winner.relevant), classMemberId: survivorId };
        const survivorRows = results.filter((r) => r.classMemberId === survivorId && r.id);
        if (survivorRows.length === 0) {
            copies.push({ testId, content });
            return;
        }
        survivorRows.forEach((row) =>
            overwrites.push({
                testId,
                resultId: row.id as string,
                content: { ...content, id: row.id, ...shareOf(row, winner.relevant) },
            })
        );
    });

    return { survivorId, droppedIds, copies, overwrites, hasConflicts };
};

/** Sequential on purpose: a failure must leave a state a retry can finish, and deletes come last. */
export const executeClassMemberMerge = async (plan: MergePlan, io: MergeIo): Promise<void> => {
    for (const { testId, content } of plan.copies) {
        await io.createResult(testId, content);
    }
    for (const { testId, resultId, content } of plan.overwrites) {
        await io.saveResult(testId, resultId, content);
    }
    for (const memberId of plan.droppedIds) {
        await io.deleteMember(memberId);
    }
};
